import { h, Ref } from 'vue';
import { dropdownField, inputField } from '@/ui/components/form';
import { Task, useValidation } from '@/ui/concerns';
import { EntityItem, validate } from '@/entities/entityItem';
import { apiErrorMessage, box, pageSubtitle } from '@/ui/components';
import { t } from '@/ui/i18n/i18n';
import { hasFeature, hasPermission, Permissions } from '@/ui/auth/auth';
import { SelectOptions } from '@/ui/types';
import { LoggedInUser } from '@/entities/auth/loggedInUser';
import { chooseUserField } from '@/ui/components/user/ChooseUserField';
import { CreateUserModelIdentifier } from '@/ui/components/user/CreateUserModal';
import EntityItemPropertiesSection from '@/ui/components/entityItem/EntityItemPropertiesSection.vue';
import { EntityItemPropertyValueForm } from '@/entities/entityItemPropertyValue';
import { EntityTypeProperty } from '@/entities/entityTypeProperty';

export function useEntityItemForm(
  record: Ref<EntityItem>,
  task: Task,
  onEntityTypeChange: (value: number) => void,
  user?: LoggedInUser,
) {
  const showWifiCredentials = !user || (user && !user._legacy_has_hardware);

  const {
    validationErrors,
    hasNoValidationErrors,
    showValidationErrors,
  } = useValidation(validate, record, task.state);

  const handleSubmit = () => {
    showValidationErrors.value = true;
    if (hasNoValidationErrors.value) {
      task.run();
    }
  };

  const entityItemWifiData = () => [
    pageSubtitle(t('entity-item.wifi-credentials'), 'mt-10 mb-4'),
    inputField(
      {
        name: 'primarySsid',
        label: t('entity-item.wifi-primary'),
        placeholder: t('entity-item.enter-wifi-ssid'),
        value: record.value.primarySsid,
        required: false,
        errors: validationErrors.value,
        disabled: task.state.value.pending,
      },
      value => {
        record.value.primarySsid = value;
      },
    ),

    inputField(
      {
        name: 'primaryPassword',
        type: 'password',
        label: t('entity-item.wifi-password'),
        placeholder: t('entity-item.enter-wifi-password'),
        value: record.value.primaryPassword,
        required: false,
        errors: validationErrors.value,
        disabled: task.state.value.pending,
      },

      value => {
        record.value.primaryPassword = value;
      },
    ),

    inputField(
      {
        name: 'secondarySsid',
        label: t('entity-item.wifi-secondary'),
        placeholder: t('entity-item.enter-wifi-ssid'),
        value: record.value.secondarySsid,
        required: false,
        errors: validationErrors.value,
        disabled: task.state.value.pending,
      },

      value => {
        record.value.secondarySsid = value;
      },
    ),

    inputField(
      {
        name: 'secondaryPassword',
        type: 'password',
        label: t('entity-item.wifi-password-sec'),
        placeholder: t('entity-item.enter-wifi-password-sec'),
        value: record.value.secondaryPassword,
        required: false,
        errors: validationErrors.value,
        disabled: task.state.value.pending,
      },

      value => {
        record.value.secondaryPassword = value;
      },
    ),
  ];

  const formFields = (
    userOptions: SelectOptions,
    entityTypeOptions: SelectOptions,
    entityTypeProperties: EntityTypeProperty[],
  ) => [
    box([
      pageSubtitle(t('entity-item.general-information'), 'mb-4'),

      inputField(
        {
          name: 'title',
          label: t('entity-item.title'),
          placeholder: t('entity-item.enter-title'),
          value: record.value.title,
          required: true,
          errors: validationErrors.value,
          disabled: !hasPermission(Permissions.MANAGE_ENTITY_ITEMS) || task.state.value.pending,
        },

        value => {
          record.value.title = value;
        },
      ),

      inputField(
        {
          type: 'textarea',
          name: 'description',
          label: t('common.description'),
          placeholder: t('entity-item.enter-description'),
          value: record.value.description,
          required: false,
          errors: validationErrors.value,
          disabled: !hasPermission(Permissions.MANAGE_ENTITY_ITEMS) || task.state.value.pending,
        },

        value => {
          record.value.description = value;
        },
      ),

      chooseUserField(
        {
          props: {
            value: record.value.users,
            options: userOptions,
            errors: validationErrors.value,
            disabled: !hasPermission(Permissions.MANAGE_ENTITY_ITEMS) || task.state.value.pending,
            required: false,
            placeholder: t('entity-item.to-user'),
            identifier: CreateUserModelIdentifier.entityItemCreator,
          },
          on: {
            input: (value: number[]) => {
              record.value.users = value;
            },
          },
        },
      ),

      dropdownField(
        {
          name: 'entityType',
          label: t('common.entity-type'),
          value: record.value.entityTypeId,
          placeholder: t('entity-item.enter-entity-type'),
          required: true,
          options: entityTypeOptions,
          errors: validationErrors.value,
          disabled: !hasPermission(Permissions.MANAGE_ENTITY_ITEMS) || task.state.value.pending,
        },

        value => {
          record.value.entityTypeId = value;
          onEntityTypeChange(value);
        },
      ),
    ], {
      class: 'entity-item-op-box',
    }),

    entityTypeProperties.length ? box([
      h(EntityItemPropertiesSection, {
        props: {
          properties: entityTypeProperties,
          propertyValues: record.value.properties,
          errors: validationErrors.value,
          disabled: task.state.value.pending,
        },
        on: {
          input: (properties: EntityItemPropertyValueForm[]) => {
            record.value.properties = properties;
          },
        },
      }),
    ]) : [],

    ...hasFeature('sensors') && showWifiCredentials ? entityItemWifiData() : [],

    apiErrorMessage(task.state.value),
  ];

  return {
    formFields,
    handleSubmit,
  };
}
